export const COLORS = {
  bg: "#F4F4F0",
  bgAlt: "#ede5d6",
  ink: "#1a2330",
  inkSoft: "#4a5568",
  inkMuted: "#6b7280",
  dark: "#0e2030",
  darkInk: "#e6ecf3",
  darkInkMuted: "#9aa9b8",
  card: "#ffffff",
  border: "rgba(15, 30, 45, 0.08)",
  borderStrong: "rgba(15, 30, 45, 0.16)",
  highlight: "#e8efea",
};

export const SERIF = "'Playfair Display', 'Source Serif Pro', Georgia, 'Times New Roman', serif";
export const SANS = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Inter, system-ui, sans-serif";
export const MONO = "'JetBrains Mono', 'Fira Code', monospace";

// Canonical sticky-header chrome — SiteHeader (every public page) and the
// admin console's AdminHeader share these values so the logo/padding/background
// don't shift when navigating between pages.
export const HEADER = {
  background: "rgba(244, 244, 240, 0.92)",
  backdropFilter: "saturate(160%) blur(10px)",
  zIndex: 100,
  paddingDesktop: "14px 24px",
  paddingMobile: "14px 20px",
  logoSize: 22,
};

// Canonical type for every item in a header bar — nav links, plain-text
// buttons, bordered pills, the primary CTA and the username all render exactly
// this (the spec comes from "Sign in"). Only the container differs: fill,
// border, or nothing. Badges (env, ADMIN) are not nav items and keep their own
// small uppercase treatment. Spread it, don't override its keys per item.
export const NAV_ITEM = {
  fontFamily: SANS,
  fontSize: 14,
  fontWeight: 400,
  textTransform: "none",
};

export const PROVIDER_MODELS = {
  anthropic: ["claude-sonnet-4-6", "claude-opus-4-7", "claude-haiku-4-5-20251001"],
  openai: ["gpt-4o", "gpt-4o-mini"],
  gemini: ["gemini-3.1-flash-lite", "gemini-2.5-flash", "gemini-flash-latest"],
};

export const PROVIDER_COLORS = {
  anthropic: "#c96442",
  openai: "#10a37f",
  gemini: "#4285f4",
};

export const JUDGE_COLOR = "#f0c040";

// Shared form control styles used by the centered pages.
export const formStyles = {
  sectionTitle: {
    fontSize: 13,
    textTransform: "uppercase",
    letterSpacing: 2,
    color: COLORS.inkMuted,
    marginBottom: 12,
    marginTop: 0,
  },
  fieldLabel: {
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: 1.5,
    color: COLORS.inkMuted,
    marginBottom: 6,
    display: "block",
  },
  textInput: {
    width: "100%",
    background: COLORS.card,
    color: COLORS.ink,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    padding: "10px 14px",
    fontSize: 14,
    fontFamily: SANS,
    boxSizing: "border-box",
    outline: "none",
  },
  textarea: {
    width: "100%",
    background: COLORS.card,
    color: COLORS.ink,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    padding: 14,
    fontSize: 14,
    fontFamily: SANS,
    resize: "vertical",
    boxSizing: "border-box",
    outline: "none",
    lineHeight: 1.5,
  },
  select: {
    width: "100%",
    background: COLORS.card,
    color: COLORS.ink,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    padding: "10px 14px",
    fontSize: 14,
    fontFamily: SANS,
    boxSizing: "border-box",
    outline: "none",
  },
  primaryButton: {
    width: "100%",
    padding: "14px 20px",
    border: "none",
    borderRadius: 10,
    color: "#fff",
    background: COLORS.dark,
    fontFamily: SANS,
    fontWeight: 600,
    fontSize: 14,
    cursor: "pointer",
    transition: "background 0.15s, opacity 0.15s",
  },
  primaryButtonDisabled: {
    background: "#9aa3ad",
    cursor: "not-allowed",
  },
  ghostButton: {
    padding: "10px 16px",
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    color: COLORS.inkSoft,
    background: "transparent",
    fontFamily: SANS,
    fontSize: 13,
    cursor: "pointer",
  },
  fieldGroup: {
    marginBottom: 24,
  },
  rowGroup: {
    display: "flex",
    gap: 12,
    width: "100%",
    alignItems: "flex-end",
  },
  rowCol: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  },
};

// Centered page chrome.
export const pageStyles = {
  // Flex column so the footer (rendered after `shell`) sticks to the bottom
  // of the viewport on short pages instead of riding up under the content.
  root: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    background: COLORS.bg,
    color: COLORS.ink,
    fontFamily: SANS,
    fontSize: 14,
  },
  shell: {
    flex: 1,
    display: "flex",
    justifyContent: "center",
    padding: "32px 20px 64px 20px",
  },
  card: {
    width: "100%",
    maxWidth: 760,
    background: COLORS.card,
    borderRadius: 16,
    padding: "40px 44px",
    boxShadow: "0 6px 28px rgba(15, 30, 45, 0.06)",
    border: `1px solid ${COLORS.border}`,
  },
  cardWide: {
    width: "100%",
    maxWidth: 960,
    background: COLORS.card,
    borderRadius: 16,
    padding: "40px 44px",
    boxShadow: "0 6px 28px rgba(15, 30, 45, 0.06)",
    border: `1px solid ${COLORS.border}`,
  },
  pageTitle: {
    fontFamily: SERIF,
    fontSize: 32,
    fontWeight: 600,
    margin: "0 0 8px 0",
    letterSpacing: "-0.5px",
    color: COLORS.ink,
  },
  pageSubtitle: {
    fontSize: 14,
    color: COLORS.inkMuted,
    margin: "0 0 32px 0",
    lineHeight: 1.5,
  },
};
